import React, { FC } from "react";
import Layout from "../../components/layout/Layout";
import { Box, Typography } from "@mui/material";
import styled from "@emotion/styled";
import { IoIosResize } from "react-icons/io";
import { MdEditSquare, MdPalette, MdOutlinePrecisionManufacturing, MdOutlineAttachMoney, MdOutlineKitchen } from "react-icons/md";
import { GiWoodBeam } from "react-icons/gi";
import { FaPuzzlePiece, FaRegFileAlt, FaIndustry, FaChevronRight } from "react-icons/fa";
import { LuLightbulb } from "react-icons/lu";
import Realizations from "./components/Realizations";
import ScrollFadeComponent from "../../components/_common/ScrollFadeComponent";

const SectionTitle = styled(Typography)`
    padding: 8px;
    font-family: Quicksand, Nunito, Rubik, Poppins, Inter, Roboto, "Segoe UI", Arial, sans-serif;
    font-weight: 500;
    font-size: 1.8rem;
    color: #222;
    max-width: 100%;
`;


const SectionHeader: FC<{ icon: JSX.Element; label: string; sx?: object }> = ({ icon, label, sx }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', marginTop: '14px', fontWeight: 600, ...sx }}>
        {icon}
        {label}
    </Box>
);


const InfoBox: FC<{ children: React.ReactNode }> = ({ children }) => (
    <Box
        sx={{
            fontFamily: 'Nunito, Quicksand, Rubik, Poppins, Inter, Roboto, "Segoe UI", Arial, sans-serif',
            fontSize: "1.08rem",
            color: "#355C7D",
            letterSpacing: "0.01em",
            fontWeight: 300,
            m: 2,
            mt: 1
        }}
    >
        {children}
    </Box>
);


const iconStyle = { width: 20, height: 20, marginRight: 8, color: '#355C7D' };

const steps = [
    {
        label: "Konsultacja i projekt",
        icon: <FaPuzzlePiece size={45} style={{ color: "#355C7D" }} />
    },
    {
        label: "Darmowa wycena",
        icon: <FaRegFileAlt size={45} style={{ color: "#355C7D" }} />
    },
    {
        label: "Zaliczka",
        icon: <MdOutlineAttachMoney size={45} style={{ color: "#355C7D" }} />
    },
    {
        label: "Realizacja zamówienia",
        icon: <FaIndustry size={45} style={{ color: "#355C7D" }} />
    },
];


const FeatureItem: FC<{ icon: JSX.Element; children: React.ReactNode }> = ({ icon, children }) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            marginTop: '14px',
            ml: 2,
            pr: 1,
            fontWeight: 300,
            maxWidth: '100vw'
        }}
    >
        {icon}
        {children}
    </Box>
);


const CustomFurniture: FC = () => (
    <Layout title="Meble na wymiar">
        <Box sx={{ display: 'flex', justifyContent: 'center', maxWidth: '100%', }}>
            <Box sx={{
                maxWidth: '900px',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                mt: 4,
                pt: 4,
                pb: 10,
            }}>
                <ScrollFadeComponent><SectionTitle>Dlaczego meble na wymiar?</SectionTitle></ScrollFadeComponent>
                <ScrollFadeComponent variant="fadeX">
                    <FeatureItem icon={<IoIosResize style={iconStyle} />}>Maksymalne wykorzystanie przestrzeni</FeatureItem>
                    <FeatureItem icon={<MdEditSquare style={iconStyle} />}>Styl dopasowany do ciebie</FeatureItem>
                    <FeatureItem icon={<GiWoodBeam style={iconStyle} />}>Wybór materiałów, kolorów i uchwytów</FeatureItem>
                    <FeatureItem icon={<MdOutlinePrecisionManufacturing style={iconStyle} />}>Precyzyjna realizacja przez doświadczony zespół</FeatureItem>
                </ScrollFadeComponent>
                <img
                    style={{
                        width: '100%',
                        height: 'auto',
                        marginTop: '16px',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                        maxWidth: '100vw'
                    }}
                    alt="Kuchnia na wymiar"
                    src="https://gudykameble.pl/wp-content/uploads/2021/02/O-kuchnia-nr-1.jpg"
                />
                <ScrollFadeComponent>
                    <SectionTitle sx={{ mt: 4, ml: 2 }}>Jak wygląda proces?</SectionTitle>
                    <Box
                        sx={{
                            mt: 3,
                            display: 'flex',
                            justifyContent: 'center',
                            width: '100%',
                            '@media screen and (min-width:370px)': {
                                justifyContent: 'space-arround'
                            }
                        }}
                    >
                        {steps.map((step, idx) => (
                            <React.Fragment key={idx}>
                                <Box sx={{ textAlign: 'center', m: 0 }}>
                                    {step.icon}
                                    <Box sx={{ maxWidth: 100 }}>{step.label}</Box>
                                </Box>
                                {idx < steps.length - 1 && (
                                    <Box
                                        sx={{
                                            display: { xs: "none", sm: "flex", '@media (max-width:540px)': { display: "none" } },
                                            alignItems: "center",
                                        }}
                                    >
                                        <FaChevronRight
                                            size={18}
                                            style={{
                                                color: "#355C7D",
                                                margin: "0 16px",
                                                verticalAlign: "middle"
                                            }}
                                        />
                                    </Box>
                                )}
                            </React.Fragment>
                        ))}
                    </Box>
                </ScrollFadeComponent>
                <ScrollFadeComponent>
                    <Box sx={{ display: 'flex', mt: 6, justifyContent: 'center' }}>
                        <SectionTitle sx={{ mt: 4, ml: 1 }}>
                            Personalizacja:<br /> Ty wybierasz!
                        </SectionTitle>
                        <Box
                            sx={{
                                width: '100%',
                                maxWidth: 120,
                                maxHeight: 120,
                                aspectRatio: '1 / 1',
                                borderRadius: '50%',
                                marginRight: '10px',
                                flexShrink: 0,
                                overflow: 'hidden',
                                justifyContent: 'center',
                                marginTop: '40px',
                                '@media screen and (max-width:340px)': {
                                    maxWidth: 100,
                                    maxHeight: 100,
                                    aspectRatio: '1 / 1',
                                    marginTop: '50px'
                                }
                            }}
                        >
                            <img
                                src='https://sklep.swisskrono.pl/wp-content/uploads/2025/06/SWISSKRONO_Wzornik_listkowy_sensesation_2025-scaled.jpg'
                                alt=""
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain',
                                    display: 'block'
                                }}
                            />
                        </Box>
                    </Box>
                </ScrollFadeComponent>
                <ScrollFadeComponent variant="fadeX">
                    <SectionHeader icon={<GiWoodBeam style={iconStyle} />} label="Materiały" sx={{ ml: 1 }} />
                    <ul style={{
                        marginLeft: '15px',
                        padding: 0,
                        fontFamily: 'Nunito, Quicksand, Rubik, Poppins, Inter, Roboto, "Segoe UI", Arial, sans-serif',
                        fontSize: "1.08rem",
                        color: "#355C7D",
                        lineHeight: 1.9,
                        listStyle: "'–  ' inside"
                    }}>
                        <li>MDF</li>
                        <li>Lite drewno</li>
                        <li>Fornir</li>
                        <li>Laminat</li>
                        <li>Akryl</li>
                    </ul>
                </ScrollFadeComponent>
                <ScrollFadeComponent variant="fadeX">
                    <SectionHeader icon={<MdPalette style={iconStyle} />} label="Kolory" sx={{ ml: 1 }} />
                    <InfoBox>
                        Ogromny wybór kolorów i wykończeń – dopasuj meble do swojego stylu i wnętrza.
                    </InfoBox>
                </ScrollFadeComponent>
                <ScrollFadeComponent variant="fadeX">
                    <SectionHeader icon={<MdOutlineKitchen style={iconStyle} />} label="Uchwyty" sx={{ pl: 2 }} />
                    <InfoBox>
                        Wybierz spośród różnych uchwytów – od klasycznych po nowoczesne, aby idealnie dopełnić wygląd mebli.
                    </InfoBox>
                </ScrollFadeComponent>
                <ScrollFadeComponent variant="fadeX">
                    <SectionHeader icon={<LuLightbulb style={iconStyle} />} label="Rozwiązania" sx={{ ml: 2 }} />
                    <InfoBox>
                        Oferujemy różne rozwiązania, takie jak :
                        <ul style={{
                            marginLeft: '10px',
                            padding: 0,
                            fontFamily: 'Nunito, Quicksand, Rubik, Poppins, Inter, Roboto, "Segoe UI", Arial, sans-serif',
                            fontSize: "1.08rem",
                            color: "#355C7D",
                            lineHeight: 1.9,
                            listStyle: "'–  ' inside",
                            marginTop: '0px'
                        }}>
                            <li>Szuflady z cichym domykiem</li>
                            <li>Systemy cargo</li>
                            <li>Oświetlenie LED</li>
                            <li>Zabudowane kosze na pranie</li>
                            <li>Wbudowane gniazdka elektryczne</li>
                            <li>Wysuwane blaty robocze</li>
                        </ul>
                    </InfoBox>
                </ScrollFadeComponent>
                <Realizations />
            </Box>
        </Box>
    </Layout>
);

export default CustomFurniture;